/* eslint-disable eqeqeq */
/* eslint-disable no-unused-vars */
/* eslint-disable no-restricted-imports */

// ============================================================================
// IMPORTS - Librerie e moduli interni
// ============================================================================
import React, { useState, useEffect, useMemo } from "react";
/*eslint-disable-next-line*/
import { shallowEqual, useSelector } from "react-redux";
import { Chip, TextField, Avatar } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { Skeleton } from "@material-ui/lab";
import * as PropTypes from "prop-types";

// DevExpress Grid - Stati e plugin per gestione dati
import {
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  FilteringState,
  IntegratedFiltering,
  DataTypeProvider,
  RowDetailState,
} from "@devexpress/dx-react-grid";
// DevExpress Grid - Componenti UI Bootstrap 4
import {
  Grid,
  Table,
  TableHeaderRow,
  PagingPanel,
  TableFilterRow,
  TableRowDetail,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";

// Icone e UI
import SVG from "react-inlinesvg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBox, faBoxOpen, faSquareCheck } from "@fortawesome/free-solid-svg-icons";
/*eslint-disable-next-line*/
import TrackingCodeInput from "../Common/TrackingCodeInput";
import * as apiTracking from "../../api/tracking";

import { Card, Tooltip, OverlayTrigger, Button, Form, Modal } from "react-bootstrap";
import moment from "moment-timezone";

// Configurazioni e utility interne
import {
  roles,
  roleOptionsForSelect,
  icons,
  statusRichiesta,
  createMetodoPagamentoMapping,
} from "../../config/config";
import { toFloat } from "../../modules/Shop/utils/shopGridDiscounts";
//import * as apiCampioni from "../../api/campioni";
import * as apiRichiesteProdotti from "../../api/richiesteVendita";

// ============================================================================
// FUNZIONI DI UTILITÀ
// ============================================================================

/**
 * Raggruppa una lista di elementi di richiesta prodotti per idRichiesta.
 * Ogni richiesta contiene un array "cart" con tutti i prodotti associati.
 * 
 * @param {Array} list - Lista di oggetti richiesta prodotto (flat)
 * @returns {Map} Map con chiave idRichiesta e valore oggetto richiesta con cart
 */
function groupRichieste(list) {
  const map = new Map();
  
  list.forEach((item) => {
    const collection = map.get(item.idRichiesta);
    
    // Destructuring di tutti i campi dell'item per chiarezza
    const {
      idRichiesta,
      idAgente,
      status,
      lastUpdate,
      dateCreated,
      agente,
      address,
      metodoPagamento,
      note,
      quantity,
      sconto,
      idProdotto,
      prodotto,
      sku,
      division,
      image,
      price,
      iva,
      cliente,
      clienteAddress,
      clienteProvincia,
      clienteComune,
      clienteCap,
      clientePartitaIva,
      clienteCodiceFiscale,
      clienteCodicePec,
      clienteReferente,
      trackingCode,
    } = item;
    
    // Prima occorrenza: creo la struttura richiesta
    if (!collection) {
      map.set(idRichiesta, {
        idRichiesta,
        idAgente,
        status: parseInt(status),
        lastUpdate,
        dateCreated,
        agente,
        address,
        cliente,
        clienteAddress,
        clienteProvincia,
        clienteComune,
        clienteCap,
        clientePartitaIva,
        clienteCodiceFiscale,
        clienteCodicePec,
        clienteReferente,
        metodoPagamento: parseInt(metodoPagamento),
        note,
        trackingCode,
        // Array cart con il primo prodotto
        cart: [
          {
            idProdotto,
            name: prodotto,
            sku: sku,
            quantity: parseInt(quantity),
            sconto: parseFloat(sconto),
            price: parseFloat(price),
            iva: parseInt(iva, 10),
            division,
            image,
          },
        ],
      });
    } else {
      // Richiesta esistente: aggiungo solo il prodotto al cart
      collection.cart.push({
        idProdotto,
        name: prodotto,
        sku: sku,
        quantity: parseInt(quantity),
        sconto: parseFloat(sconto),
        price: parseFloat(price),
        iva: parseInt(iva, 10),
        division,
        image,
      });
    }
  });
  
  // Calcolo quantità totale per ogni richiesta sommando i prodotti nel cart
  for (let [idRichiesta, richiesta] of map) {
    map.set(idRichiesta, {
      ...richiesta,
      quantity: richiesta.cart.reduce((q, item) => q + item.quantity, 0),
    });
  }
  
  return map;
}

// ============================================================================
// COMPONENTI UI AUSILIARI (Reusable)
// ============================================================================

/**
 * Wrapper per Table di DevExpress con classi CSS personalizzate Metronic
 */
const TableComponent = ({ ...restProps }) => (
  <Table.Table 
    {...restProps} 
    className="table table-hover table-head-custom table-vertical-center overflow-hidden" 
  />
);

/**
 * Factory per componente riga con handler click per expand/collapse
 * @param {Function} clickCallback - Callback con id della riga cliccata
 * @returns {Component} Componente Row con onClick
 */
const getTableRow = (clickCallback) => {
  return ({ row, ...restProps }) => (
    <Table.Row
      {...restProps}
      // eslint-disable-next-line no-alert
      onClick={() => clickCallback(restProps.tableRow.rowId)}
      style={{ cursor: "pointer" }}
    />
  );
};

/**
 * Cell per filtro con PropTypes validati
 */
const FilterCell = (props) => {
  return <TableFilterRow.Cell {...props} />;
};
FilterCell.propTypes = {
  column: PropTypes.shape({ name: PropTypes.string }).isRequired,
};

/**
 * Formatter per colonne data: visualizza DD MMMM YYYY, HH:mm
 */
const DateFormattedColumn = (props) => (
  <DataTypeProvider 
    formatterComponent={({ value }) => moment(value).format("DD MMMM YYYY, HH:mm")} 
    {...props} 
  />
);

/**
 * Factory per pulsanti azione con tooltip opzionale
 * @param {Object} params - Configurazione pulsante
 * @returns {JSX} Button con icona e tooltip
 */
const makeActionBtn = ({ name, disabled, tooltip, callback, variant, icon, value }) => {
  const btn = (
    <Button
      variant={variant}
      className={`mx-3 my-2`}
      onClick={!disabled ? () => callback(value) : null}
      disabled={disabled}
      title={name}
    >
      {typeof icon == "string" ? (
        <span className={`svg-icon svg-icon-md svg-icon-white`}>
          {" "}
          <SVG src={toAbsoluteUrl(icon)} />
        </span>
      ) : (
        <FontAwesomeIcon icon={icon} className={`svg-icon-white`} style={{ marginRight: "0.5rem" }} />
      )}
      <span className="text-white">{name}</span>
    </Button>
  );
  
  if (!tooltip) return btn;
  return <OverlayTrigger overlay={<Tooltip>{tooltip}</Tooltip>}>{btn}</OverlayTrigger>;
};

/**
 * Componente per formattazione colonna "Stato" con Chip colorati
 * @param {Object} props - Props con value (statusRichiesta)
 * @returns {JSX} Chip con icona e stile in base allo stato
 */
const StatoColumn = ({ value }) => {
  /*eslint-disable-next-line*/
  if (value == statusRichiesta.inAttesa)
    return (
      <div className="pl-3">
        <Chip
          variant="outlined"
          label="In attesa"
          icon={<FontAwesomeIcon icon={faBoxOpen} className="svg-icon-info svg-icon-md" />}
          style={{ borderColor: "var(--info)", color: "var(--info)" }}
        />
      </div>
    );
  
  /*eslint-disable-next-line*/
  if (value == statusRichiesta.approvato)
    return (
      <div className="pl-3">
        <Chip color="success" variant="outlined" label="Approvato" icon={icons.ok} />
      </div>
    );
  
  /*eslint-disable-next-line*/
  if (value == statusRichiesta.inevaso)
    return (
      <div className="pl-3">
        <Chip
          color="warning"
          variant="outlined"
          label="Inevaso"
          icon={<FontAwesomeIcon icon={faBox} className="svg-icon-warning svg-icon-md" />}
          style={{ borderColor: "var(--warning)" }}
        />
      </div>
    );
  
  /*eslint-disable-next-line*/
  if (value == statusRichiesta.evaso)
    return (
      <div className="pl-3">
        <Chip
          variant="outlined"
          label="Evaso"
          icon={<FontAwesomeIcon icon={faSquareCheck} className="svg-icon-primary svg-icon-md" />}
          style={{ borderColor: "var(--primary)", color: "var(--primary)" }}
        />
      </div>
    );
  
  /*eslint-disable-next-line*/
  if (value == statusRichiesta.rifiutato)
    return (
      <div className="pl-3">
        <Chip
          variant="outlined"
          label="Rifiutato"
          icon={
            <span className="svg-icon svg-icon-md svg-icon-danger">
              {" "}
              <SVG src={toAbsoluteUrl(icons.close)} />
            </span>
          }
          style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
        />
      </div>
    );
  
  return <div className="pl-3"></div>;
};

export const StatoFormattedColumn = (props) => <DataTypeProvider formatterComponent={StatoColumn} {...props} />;

// ============================================================================
// COMPONENTE DETTAGLIO RIGA (Row Detail)
// ============================================================================

/**
 * Componente per visualizzazione dettagliata di una richiesta prodotto.
 * Gestisce: lista prodotti, calcoli prezzi/sconti, azioni stato, tracking code.
 * 
 * @param {Object} props - Props con callbacks e permessi
 */
const RichiestaRowDetail = ({
  canApprove,
  canManage,
  isRowLoading,
  isItemLoading,
  isEditing,
  editedValue,
  updateStatusCallback,
  updateCartQuantityCallback,
  saveCartQuantityCallback,
  removeFromCartCallback,
  cancelEditingCallback,
  updateTrackingCodeCallback,
  metodiPagamentoMapping,
}) => {
  return (
    <TableRowDetail
      contentComponent={({ row }) => {
        // ====================================================================
        // LOGICA PERMESSI AZIONI
        // ====================================================================
        const isDisabled = {
          // Modifica: agente solo se in attesa | admin solo se non evaso
          edit:
            (!canApprove && row.status != statusRichiesta.inAttesa) ||
            (canApprove && row.status == statusRichiesta.evaso),
          // Cancellazione: agente solo se in attesa | admin/mag se non evaso/rifiutato
          cancel:
            (!canManage && row.status != statusRichiesta.inAttesa) ||
            (canManage && (row.status == statusRichiesta.evaso || row.status == statusRichiesta.rifiutato)),
          // Approvazione: solo admin, e solo se non già evaso/approvato
          approve: !canApprove || row.status == statusRichiesta.evaso || row.status == statusRichiesta.approvato,
          // Imposta "Pronto": admin/mag, solo se approvato
          setInevaso: !canManage || row.status != statusRichiesta.approvato,
          // Imposta "Evaso": admin/mag, solo se approvato o inevaso
          setEvaso: !canManage || !(row.status == statusRichiesta.approvato || row.status == statusRichiesta.inevaso),
        };
        
        // Mappa quantità visualizzate: usa valore editing se presente, altrimenti quantità originale
        const shownQuantity = {};
        for (var item of row.cart) {
          shownQuantity[item.idProdotto] = isEditing(row.idRichiesta, item.idProdotto)
            ? editedValue(row.idRichiesta, item.idProdotto)
            : item.quantity;
        }

        // ====================================================================
        // RENDER DETTAGLIO RICHIESTA
        // ====================================================================
        return (
          <>
            <div className="card">
              {/* Lista prodotti nel cart */}
              {row.cart.map((item) => (
                <React.Fragment key={item.idProdotto}>
                  <div className="d-flex align-items-center justify-content-xl-between p-8">
                    <div className="d-flex flex-column mr-2 order-2 order-xl-1 ml-2">
                      {/* SKU + Nome prodotto + Bottone rimuovi */}
                      <span className="font-weight-bold text-dark-75 font-size-lg text-hover-primary">
                        <span className="text-primary">{item.sku}</span> {item.name}
                        {isDisabled.edit ? null : (
                          <Button
                            variant="light-success"
                            className="btn-xs btn-icon ml-2"
                            disabled={isItemLoading(row.idRichiesta, item.idProdotto)}
                            onClick={() => removeFromCartCallback(row.idRichiesta, item.idProdotto)}
                          >
                            <i className="ki ki-close icon-xs"></i>
                          </Button>
                        )}
                      </span>
                      
                      {/* Sconto prodotto se presente */}
                      {item.sconto ? (
                        <div className="d-flex align-items-center mt-2">Sconto: {item.sconto}%</div>
                      ) : null}
                      
                      {/* Prezzo unitario × quantità = totale riga */}
                      <div className="d-flex align-items-center mt-2">
                        <span className="mr-2 text-dark-75 font-size-3">
                          {typeof item.price !== "undefined" ? `${item.price}€` : "-€"}
                        </span>
                        <span className="text-muted mr-1"> &#215; </span>
                        <span className={"font-weight-bold mr-1 text-dark-75 font-size-3"}>
                          {isEditing(row.idRichiesta, item.idProdotto) ? (
                            <>
                              {editedValue(row.idRichiesta, item.idProdotto) !== 1
                                ? `${editedValue(row.idRichiesta, item.idProdotto)} colli`
                                : "1 collo"}
                            </>
                          ) : (
                            <> {shownQuantity[item.idProdotto]} </>
                          )}
                        </span>
                        <span className="text-muted mr-2">=</span>
                        <span className="mr-2 text-dark-75 font-size-3">
                          {typeof item.price !== "undefined" ? (
                            item.sconto > 0 ? (
                              <>
                                <span style={{ textDecoration: "line-through" }}>
                                  {(shownQuantity[item.idProdotto] * item.price).toFixed(2)}€
                                </span>
                                <span className="ml-2">
                                  {((1 - item.sconto / 100) * item.price * shownQuantity[item.idProdotto]).toFixed(2)}
                                </span>
                              </>
                            ) : (
                              `${(shownQuantity[item.idProdotto] * item.price).toFixed(2)}€`
                            )
                          ) : (
                            "-€"
                          )}
                        </span>
                      </div>
                      
                      {/* Controlli quantità: +/- e salva/annulla editing */}
                      <div className="d-flex align-items-center mt-2">
                        {isDisabled.edit ? null : (
                          <Button
                            variant="light-success"
                            className="btn-xs btn-icon"
                            disabled={isItemLoading(row.idRichiesta, item.idProdotto)}
                            onClick={() =>
                              updateCartQuantityCallback(
                                row.idRichiesta,
                                item.idProdotto,
                                Math.max(1, shownQuantity[item.idProdotto] - 1)
                              )
                            }
                          >
                            <i className="ki ki-minus icon-xs"></i>
                          </Button>
                        )}
                        {isDisabled.edit ? null : (
                          <Button
                            variant="light-success"
                            className="btn-xs btn-icon ml-2"
                            disabled={isItemLoading(row.idRichiesta, item.idProdotto)}
                            onClick={() =>
                              updateCartQuantityCallback(
                                row.idRichiesta,
                                item.idProdotto,
                                shownQuantity[item.idProdotto] + 1
                              )
                            }
                          >
                            <i className="ki ki-plus icon-xs"></i>
                          </Button>
                        )}
                        {!isDisabled.edit && isEditing(row.idRichiesta, item.idProdotto) ? (
                          <Button
                            variant="success"
                            className="btn-xs btn-icon ml-2"
                            disabled={isItemLoading(row.idRichiesta, item.idProdotto)}
                            onClick={() => saveCartQuantityCallback(row.idRichiesta, item.idProdotto)}
                          >
                            <i className="ki ki-check icon-xs"></i>
                          </Button>
                        ) : null}
                        {!isDisabled.edit && isEditing(row.idRichiesta, item.idProdotto) ? (
                          <Button
                            variant="light-danger"
                            className="btn-xs btn-icon ml-2"
                            disabled={isItemLoading(row.idRichiesta, item.idProdotto)}
                            onClick={() => cancelEditingCallback(row.idRichiesta, item.idProdotto)}
                          >
                            <i className="ki ki-close icon-xs"></i>
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    
                    {/* Immagine prodotto se disponibile */}
                    {typeof item.image !== "undefined" && item.image !== "" && item.image !== "0" ? (
                      <div className="symbol symbol-70 order-xl-2">
                        <img src={toAbsoluteUrl(item.image)} alt="" />
                      </div>
                    ) : null}
                  </div>
                  <div className="separator separator-solid"></div>
                </React.Fragment>
              ))}
              
              {/* ====================================================================
                  SEZIONE RIASSUNTIVA: Totali e Informazioni Cliente
                  ==================================================================== */}
              <div className="p-8">
                {/* Totale quantità */}
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Numero totale</span>
                  <span className="font-weight-bolder text-primary text-right">
                    {row.cart.reduce((q, item) => q + shownQuantity[item.idProdotto], 0)}
                  </span>
                </div>
                
                {/* Valore totale (senza sconti metodo pagamento) */}
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Valore totale</span>
                  <span className="font-weight-bolder text-dark-50 text-right">
                    {row.cart
                      .reduce(
                        (sum, item) =>
                          typeof item.price !== "undefined" ? sum + item.price * shownQuantity[item.idProdotto] : sum,
                        0
                      )
                      .toFixed(2)}
                    €
                  </span>
                </div>
                
                {/* Valore totale con sconti (prodotto + metodo pagamento + IVA) */}
                <div className="d-flex align-items-center justify-content-xl-between mb-7">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Valore totale scontato</span>
                  <span className="font-weight-bolder text-dark-50 text-right">
                    {(() => {
                      const metodoPagamentoDiscount = toFloat(metodiPagamentoMapping?.discounts?.[row.metodoPagamento], 0);
                      
                      // Calcolo totale con IVA e sconto prodotto
                      const totalWithIva = row.cart.reduce((sum, item) => {
                        if (typeof item.price === "undefined") return sum;
                        const quantity = toFloat(shownQuantity[item.idProdotto], 0);
                        const price = toFloat(item.price, 0);
                        const itemDiscount = toFloat(item.sconto, 0);
                        const iva = toFloat(item.iva, 0);

                        const base = price * quantity;
                        const afterItemDiscount = base * (itemDiscount > 0 ? 1 - itemDiscount / 100 : 1);
                        const afterIva = afterItemDiscount * (1 + iva / 100);
                        return sum + afterIva;
                      }, 0);

                      // Se esiste sconto metodo pagamento, applico ulteriore discount
                      if (metodoPagamentoDiscount > 0) {
                        const totalWithMetodoPagamentoDiscount = row.cart.reduce((sum, item) => {
                          if (typeof item.price === "undefined") return sum;
                          const quantity = toFloat(shownQuantity[item.idProdotto], 0);
                          const price = toFloat(item.price, 0);
                          const itemDiscount = toFloat(item.sconto, 0);
                          const iva = toFloat(item.iva, 0);

                          const base = price * quantity;
                          const afterItemDiscount = base * (itemDiscount > 0 ? 1 - itemDiscount / 100 : 1);
                          const afterMetodoPagamentoDiscount = afterItemDiscount * (1 - metodoPagamentoDiscount);
                          const afterIva = afterMetodoPagamentoDiscount * (1 + iva / 100);
                          return sum + afterIva;
                        }, 0);

                        const percent = metodoPagamentoDiscount * 100;
                        const percentLabel = Number.isInteger(percent) ? String(percent) : percent.toFixed(1);

                        return (
                          <>
                            <span style={{ textDecoration: "line-through" }}>{totalWithIva.toFixed(2)}</span>
                            <span> + Sconto {percentLabel}%: </span>
                            <span className="ml-2">{totalWithMetodoPagamentoDiscount.toFixed(2)}</span>
                            <span>€</span>
                          </>
                        );
                      }

                      return <span>{totalWithIva.toFixed(2)}€</span>;
                    })()}
                  </span>
                </div>
                
                {/* Informazioni Agente e Cliente */}
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Agente</span>
                  <span className="font-weight-bolder text-dark-50 text-right">{row.agente}</span>
                </div>
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Indirizzo Agente</span>
                  <span className="font-weight-bolder text-dark-50 text-right">{row.address}</span>
                </div>
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Cliente</span>
                  <span className="font-weight-bolder text-dark-50 text-right">{row.cliente}</span>
                </div>
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Indirizzo Cliente</span>
                  <span className="font-weight-bolder text-dark-50 text-right">
                    {[
                      !!row.clienteAddress ? row.clienteAddress : "",
                      !!row.clienteComune || !!row.clienteProvincia
                        ? (!!row.clienteComune ? row.clienteComune : "") +
                          (!!row.clienteProvincia ? " (" + row.clienteProvincia + ")" : "")
                        : "",
                      !!row.clienteCap ? row.clienteCap : "",
                    ]
                      .filter((s) => !!s)
                      .join(", ")}
                  </span>
                </div>
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Partita IVA</span>
                  <span className="font-weight-bolder text-dark-50 text-right">{row.clientePartitaIva}</span>
                </div>
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Codice Fiscale</span>
                  <span className="font-weight-bolder text-dark-50 text-right">{row.clienteCodiceFiscale}</span>
                </div>
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Codice SDI / PEC</span>
                  <span className="font-weight-bolder text-dark-50 text-right">{row.clienteCodicePec}</span>
                </div>
                {row.clienteReferente ? (
                  <div className="d-flex align-items-center justify-content-xl-between mb-4">
                    <span className="font-weight-bold text-muted font-size-sm mr-2">Referente consegna</span>
                    <span className="font-weight-bolder text-dark-50 text-right">{row.clienteReferente}</span>
                  </div>
                ) : null}
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Metodo di pagamento</span>
                  <span className="font-weight-bolder text-dark-50 text-right">
                    {metodiPagamentoMapping?.[row.metodoPagamento] ?? "-"}
                  </span>
                </div>
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bold text-muted font-size-sm mr-2">Note</span>
                </div>
                <div className="d-flex align-items-center justify-content-xl-between mb-4">
                  <span className="font-weight-bolder text-dark-50 text-xl-right">{row.note}</span>
                </div>
              </div>
              
              <div className="separator separator-solid"></div>
              <div className="separator separator-solid"></div>

              {/* Input Tracking Code */}
              <TrackingCodeInput
                canManage={canApprove}
                row={row}
                isRowLoading={isRowLoading}
                updateTrackingCodeCallback={updateTrackingCodeCallback}
              />
              <div className="separator separator-solid"></div>

              {/* ====================================================================
                  PULSANTI AZIONE STATO RICHIESTA
                  ==================================================================== */}
              <div>
                <div className="float-xl-right">
                  {makeActionBtn({
                    name: canApprove ? "Rifiuta" : "Annulla",
                    callback: (id) => updateStatusCallback(id, statusRichiesta.rifiutato),
                    disabled: isRowLoading(row.idRichiesta) || isDisabled.cancel,
                    variant: "danger",
                    icon: icons.close,
                    value: row.idRichiesta,
                  })}
                  {canManage
                    ? makeActionBtn({
                        name: "Approva",
                        callback: (id) => updateStatusCallback(id, statusRichiesta.approvato),
                        disabled: isRowLoading(row.idRichiesta) || isDisabled.approve,
                        variant: "success",
                        icon: icons.ok,
                        value: row.idRichiesta,
                      })
                    : null}
                  {canManage
                    ? makeActionBtn({
                        name: "Pronto",
                        tooltip: "Pronto per ritiro/spedizione",
                        callback: (id) => updateStatusCallback(id, statusRichiesta.inevaso),
                        disabled: isRowLoading(row.idRichiesta) || isDisabled.setInevaso,
                        variant: "warning",
                        icon: faBox,
                        value: row.idRichiesta,
                      })
                    : null}
                  {canManage
                    ? makeActionBtn({
                        name: "Evaso",
                        callback: (id) => updateStatusCallback(id, statusRichiesta.evaso),
                        disabled: isRowLoading(row.idRichiesta) || isDisabled.setEvaso,
                        variant: "primary",
                        icon: faSquareCheck,
                        value: row.idRichiesta,
                      })
                    : null}
                </div>
              </div>
            </div>
          </>
        );
      }}
    />
  );
};

// ============================================================================
// CONFIGURAZIONE GRID - Colonne, Messaggi, Paging
// ============================================================================

// Definizione colonne visibili nella tabella principale
const prodottiColumns = [
  { name: "agente", title: "Agente" },
  { name: "status", title: "Stato" },
  { name: "cliente", title: "Cliente" },
  { name: "lastUpdate", title: "Ultima Modifica" },
];

// Estensioni colonne: word-wrap e larghezze personalizzate
const columnExtensions = [
  { columnName: "agente", wordWrapEnabled: true },
  { columnName: "status", width: "auto" },
  { columnName: "cliente", wordWrapEnabled: true },
  { columnName: "lastUpdate", wordWrapEnabled: true },
];

// Messaggi localizzati per la grid
const tableMessages = {
  noData: "Nessuna richiesta effettuata",
};
const filterRowMessages = {
  filterPlaceholder: "Filtra...",
};
const pagingPanelMessages = {
  showAll: "Tutte",
  rowsPerPage: "Righe per pagina",
  info: "Da {from} a {to} ({count} richieste)",
};

// Configurazione paging e sorting iniziale
const pageSizes = [10, 20, 30, 0]; // 0 = tutte
const initialPageSize = 10;
const initialSorting = [{ columnName: "lastUpdate", direction: "desc" }];

// ============================================================================
// COMPONENTE PRINCIPALE: RichiesteProdotti
// ============================================================================

export const RichiesteProdotti = () => {
  // ==========================================================================
  // STATE MANAGEMENT - Dati tabella e loading
  // ==========================================================================
  const [rows, setRows] = useState([]); // Dati raggruppati da visualizzare
  const [loading, setLoading] = useState(false); // Loading iniziale fetch
  const [loadingRows, setLoadingRows] = useState({}); // Tracking loading per singola riga
  const [loadingItems, setLoadingItems] = useState({}); // Tracking loading per singolo prodotto
  const [editingItems, setEditingItems] = useState({}); // Tracking quantità in editing
  const [_internalUpdateCount, _setInternalUpdateCount] = useState(0); // Trigger per re-fetch

  // ==========================================================================
  // USER & PERMISSIONS - Da Redux
  // ==========================================================================
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const role = parseInt(user.idRole);
  
  // Mapping metodi di pagamento con sconti, memoizzato per performance
  const metodiPagamentoMapping = useMemo(() => createMetodoPagamentoMapping(roles, role), [role]);

  // Colonne visibili in base al ruolo: admin/mag/contabile vedono "Agente"
  const columns =
    role === roles.admin || role === roles.gestore || role === roles.developer || role === roles.magazziniere || role === roles.contabile
      ? prodottiColumns
      : prodottiColumns.slice(1);

  // ==========================================================================
  // API & DATA FETCHING
  // ==========================================================================
  
  /**
   * Fetch dati da API e aggiornamento state
   */
  const updateFromServer = () => {
    setLoading(true);
    apiRichiesteProdotti
      .getAll(user.authToken, "prodotti")
      .then((data) => {
        // Ordinamento per ultima modifica decrescente
        const sortedData = data.sort((a, b) => {
          return new Date(b.lastUpdate) - new Date(a.lastUpdate);
        });
        // Raggruppamento e aggiornamento state
        setRows(groupRichieste(sortedData));
      })
      .then(() => setLoading(false));
  };
  
  // Fetch iniziale e su trigger update
  useEffect(() => {
    updateFromServer();
    /*eslint-disable-next-line*/
  }, [_internalUpdateCount]);

  /**
   * Trigger per re-fetch dati (incrementa counter)
   */
  const doUpdate = () => {
    _setInternalUpdateCount(_internalUpdateCount + 1);
  };

  // ==========================================================================
  // UTILS LOADING & EDITING STATE
  // ==========================================================================
  
  // Helpers per gestire stato loading righe
  const addLoadingRow = (id) => {
    const clone = { ...loadingRows };
    clone[id] = true;
    setLoadingRows(clone);
  };
  const removeLoadingRow = (id) => {
    const clone = { ...loadingRows };
    delete clone[id];
    setLoadingRows(clone);
  };

  // Helpers per gestire stato loading singoli prodotti
  const addLoadingItem = (id, idProdotto) => {
    const clone = { ...loadingItems };
    if (!(id in clone)) clone[id] = {};
    clone[id][idProdotto] = true;
    setLoadingItems(clone);
  };
  const removeLoadingItem = (id, idProdotto) => {
    const clone = { ...loadingItems };
    if (id in clone && idProdotto in clone[id]) {
      delete clone[id][idProdotto];
      if (!Object.keys(clone[id]).length) delete clone[id];
    }
    setLoadingItems(clone);
  };

  // Helpers per gestire stato editing quantità
  const addEditingItem = (id, idProdotto, quantity) => {
    const clone = { ...editingItems };
    if (!(id in clone)) clone[id] = {};
    clone[id][idProdotto] = quantity;
    setEditingItems(clone);
  };
  const removeEditingItem = (id, idProdotto) => {
    const clone = { ...editingItems };
    delete clone[id][idProdotto];
    if (!clone[id]) delete clone[id];
    setEditingItems(clone);
  };

  // ==========================================================================
  // STATE GRID: Paging, Sorting, Expanded Rows
  // ==========================================================================
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sorting, setSorting] = useState(initialSorting);
  const [expandedRows, setExpandedRows] = useState([]);

  /*eslint-disable-next-line*/
  return (
    <>
      <Card className="card-custom gutter-b">
        <Card.Header className="border-0 py-5 row">
          <Card.Title className="float-left col-auto">
            <div>
              <h3 className="font-weight-bolder text-dark">Richieste di Prodotti</h3>
              <h6 className="font-weight-light text-dark mobile-only">
                Scorri a destra la tabella per altre informazioni
              </h6>
            </div>
          </Card.Title>
        </Card.Header>

        <Card.Body className="pt-0">
          {!loading ? (
            // ==================================================================
            // DEVEXPRESS GRID CONFIGURATION
            // ==================================================================
            <Grid rows={[...rows.values()].map((row) => ({ ...row }))} columns={columns}>
              {/* Formatters personalizzati */}
              <DateFormattedColumn for={["lastUpdate", "dateCreated"]} />
              <StatoFormattedColumn for={["status"]} />
              
              {/* Stati della grid */}
              <RowDetailState expandedRowIds={expandedRows} onExpandedRowIdsChange={setExpandedRows} />
              <FilteringState />
              <IntegratedFiltering />
              <PagingState
                currentPage={currentPage}
                onCurrentPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
              <IntegratedPaging />
              <SortingState sorting={sorting} onSortingChange={setSorting} />
              <IntegratedSorting />
              
              {/* Componenti UI Grid */}
              <Table
                messages={tableMessages}
                tableComponent={TableComponent}
                columnExtensions={columnExtensions}
                rowComponent={getTableRow((id) =>
                  setExpandedRows(
                    expandedRows.includes(id) 
                      ? [...expandedRows].filter((v) => v != id) 
                      : [...expandedRows, id]
                  )
                )}
              />
              <TableHeaderRow showSortingControls />
              <TableFilterRow messages={filterRowMessages} cellComponent={FilterCell} />
              
              {/* Row Detail con logica business */}
              <RichiestaRowDetail
                /*eslint-disable-next-line*/
                canApprove={role === roles.admin || role === roles.gestore || role === roles.developer || role === roles.magazziniere}
                canManage={role === roles.admin || role === roles.gestore || role === roles.developer || role === roles.magazziniere}
                isRowLoading={(id) => id in loadingRows}
                isItemLoading={(id, idProdotto) => id in loadingItems && idProdotto in loadingItems[id]}
                isEditing={(id, idProdotto) => id in editingItems && idProdotto in editingItems[id]}
                editedValue={(id, idProdotto) => editingItems[id][idProdotto]}
                metodiPagamentoMapping={metodiPagamentoMapping}
                
                // Callback aggiornamento stato richiesta
                updateStatusCallback={(id, status) => {
                  addLoadingRow(id);
                  apiRichiesteProdotti.update(user.authToken, id, status).then(() => {
                    removeLoadingRow(id);
                    doUpdate();
                  });
                }}
                
                // Callback aggiornamento tracking code
                updateTrackingCodeCallback={(id, trackingCode) => {
                  addLoadingRow(id);
                  apiTracking.updateTrackingCode(user.authToken, id, trackingCode, "vendita").then(() => {
                    removeLoadingRow(id);
                    doUpdate();
                  });
                }}
                
                // Callback editing quantità (temporaneo, solo UI)
                updateCartQuantityCallback={(id, idProdotto, quantity) => {
                  addEditingItem(id, idProdotto, quantity);
                }}
                
                // Callback salvataggio quantità su API
                saveCartQuantityCallback={(id, idProdotto) => {
                  addLoadingItem(id, idProdotto);
                  let newQuantity = editingItems[id][idProdotto];
                  apiRichiesteProdotti.updateCartQuantity(user.authToken, id, idProdotto, newQuantity).then(() => {
                    removeLoadingItem(id, idProdotto);
                    doUpdate();
                    removeEditingItem(id, idProdotto);
                  });
                }}
                
                // Callback annulla editing
                cancelEditingCallback={(id, idProdotto) => {
                  removeEditingItem(id, idProdotto);
                }}
                
                // Callback rimozione prodotto dal cart
                removeFromCartCallback={(id, idProdotto) => {
                  addLoadingItem(id, idProdotto);
                  apiRichiesteProdotti.removeFromCart(user.authToken, id, idProdotto).then(() => {
                    removeLoadingItem(id, idProdotto);
                    doUpdate();
                  });
                }}
              />
              
              {/* Paging Panel */}
              <PagingPanel messages={pagingPanelMessages} pageSizes={pageSizes} />
            </Grid>
          ) : (
            // Loading skeleton mentre fetch dati
            <div>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="rect" height={518} />
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};
